import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload } from "lucide-react";
import { useCatalogue, displayName } from "../site/useCatalogue";
import { W2 } from "../site/fx";
import { EnquiryFormFields } from "../site/EnquiryPanel";
import { useSEO } from "../seo";
import { sendOemRequest } from "../lib/forms";

const FINISHES = [
  { name: "Mirror", c: "linear-gradient(135deg,#dfe4e8,#9aa4ab 45%,#f2f5f7 60%,#7e8991)" },
  { name: "Satin", c: "linear-gradient(135deg,#c3cacf,#98a1a8)" },
  { name: "Hammered", c: "radial-gradient(circle at 30% 30%,#cfd6da 20%,#8e989f 60%,#b9c1c6)" },
  { name: "Gold", c: "linear-gradient(135deg,#e8c573,#a8813a 50%,#f0d491)" },
  { name: "Copper-tone", c: "linear-gradient(135deg,#d9906a,#9c5a3c 55%,#eab28f)" },
];

const STEPS = ["Base product", "Finish", "Your logo", "Quantity & contact"];

export default function CustomOem() {
  useSEO("Custom & OEM — Sepla Hotelware", "Custom sizes, finishes and private-label branding on Sepla stainless steel hotelware. Send a photo or sketch.");
  const { products } = useCatalogue();
  const [step, setStep] = useState(0);
  const [base, setBase] = useState(null);
  const [finish, setFinish] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", business_type: "", phone: "", email: "", city: "", message: "" });
  const [qty, setQty] = useState("");
  const [sending, setSending] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const fileRef = useRef(null);

  const bases = useMemo(() => products.filter((p) => !catFilter || p.category === catFilter), [products, catFilter]);

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    if (f.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = () => setPreview(r.result);
      r.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const canNext = step === 0 ? !!base : step === 1 ? !!finish : step === 2 ? true : form.name && form.phone && qty;

  const submit = async () => {
    if (sending) return;
    setSending(true);
    try {
      await sendOemRequest(form, { base, finish, qty, file });
      toast.success("Custom request sent", { description: "We reply with details and a quotation." });
      setStep(0); setBase(null); setFinish(""); setFile(null); setPreview(null); setQty("");
      setForm({ name: "", company: "", business_type: "", phone: "", email: "", city: "", message: "" });
    } catch (err) {
      toast.error(err?.message?.includes("too large") ? err.message : "Could not send — please try again or use WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="page" data-testid="oem-page">
      <header className="page-head">
        <p className="kicker">Custom &amp; OEM</p>
        <W2 as="h1" parts={[{ t: "Need a different size, finish or " }, { t: "your own logo?", em: true }]} className="page-title" />
        <p className="page-sub">We customise sizes and finishes, engrave your brand and pack to your specification. Send us a photo or a sketch.</p>
      </header>

      <div className="oem" data-testid="oem-wizard">
        <ol className="oem-steps">
          {STEPS.map((s, i) => (
            <li key={s} className={i === step ? "on" : i < step ? "done" : ""} data-testid={`oem-step-${i + 1}`}>
              <b>{String(i + 1).padStart(2, "0")}</b><span>{s}</span>
            </li>
          ))}
        </ol>

        <div className="oem-body">
          {step === 0 && (
            <div>
              <div className="chips oem-cats">
                <button className={"chip" + (catFilter === "" ? " on" : "")} onClick={() => setCatFilter("")}>All</button>
                {[...new Set(products.map((p) => p.category))].map((c) => (
                  <button key={c} className={"chip" + (catFilter === c ? " on" : "")} onClick={() => setCatFilter(c)}>{c.replace(/-/g, " ")}</button>
                ))}
              </div>
              <div className="oem-bases">
                {bases.map((p) => (
                  <button key={p.code} className={"oem-base" + (base?.code === p.code ? " on" : "")} onClick={() => setBase(p)} data-testid={`oem-base-${p.code}`}>
                    <img src={p.image} alt={p.name} loading="lazy" />
                    <span>{displayName(p.name)}</span>
                    <em>{p.code}</em>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="oem-finishes">
              {FINISHES.map((f) => (
                <button key={f.name} className={"oem-finish" + (finish === f.name ? " on" : "")} onClick={() => setFinish(f.name)} data-testid={`oem-finish-${f.name}`}>
                  <i style={{ background: f.c }} />
                  <span>{f.name}</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="oem-logo-step">
              <button className="oem-upload" onClick={() => fileRef.current.click()} data-testid="oem-upload">
                <Upload size={20} />
                <span>{file ? file.name : "Upload your logo, photo or sketch"}</span>
                <input ref={fileRef} type="file" accept="image/*,.pdf,.svg" hidden onChange={(e) => onFile(e.target.files[0])} />
              </button>
              <div className="etch-preview" data-testid="etch-preview">
                <div className="etch-steel">
                  {preview ? (
                    <img src={preview} alt="Logo preview etched on steel" className="etch-logo" />
                  ) : (
                    <span className="etch-hint">Your logo, laser-etched</span>
                  )}
                </div>
                <p className="etch-cap">Live preview — etched on {base ? base.name : "steel"}</p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="oem-summary">
                <span>{base ? `${base.name} · ${base.code}` : "Any product"}</span>
                <span>{finish || "Finish flexible"}</span>
                <span>{file ? `Artwork: ${file.name}` : "No artwork"}</span>
              </div>
              <label className="oem-qty">
                <span>Estimated quantity *</span>
                <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="e.g. 500 pieces" data-testid="oem-qty" />
              </label>
              <EnquiryFormFields form={form} setForm={setForm} prefix="oem" />
            </div>
          )}
        </div>

        <div className="oem-nav">
          <button className="btn btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="oem-back"><ArrowLeft size={14} /> Back</button>
          {step < 3 ? (
            <button className="btn btn-accent" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="oem-next">Next <ArrowRight size={14} /></button>
          ) : (
            <button className="btn btn-accent" disabled={!canNext || sending} onClick={submit} data-testid="oem-submit">
              {sending ? "Sending…" : "Send custom request"}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
